/**
 * The knowledge list: search, filter by status, filter by category, open, delete.
 *
 * What is built here is exactly what `GET /notes` supports. Search is `?q=`, and the API requires
 * at least two characters, so a one-character query is held back rather than sent and rejected.
 * The two stored statuses — draft and published — go to the server as `?status=`; the two derived
 * ones do not exist there and are applied here, which is stated on the screen rather than hidden,
 * because "edited" and "flagged" behave differently from the other three under a text search.
 *
 * **The category filter is applied here, and that is why this screen reads every page.**
 * `GET /notes` has no `category` parameter — the filter is not in the contract, and inventing one
 * as a query string would send something the router would ignore. So `listAllNotes` pages until
 * the server runs out and the category narrows that set. Filtering one fifty-row page and calling
 * the result "Rules" would silently hide the fifty-first rule.
 *
 * What is **not** built, and why it is not a bug: **per-category counts are in the sidebar, not
 * here**, and the heading count is `rows.length`, which is exactly the number of rows below it.
 */

import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

import { CategoryIcon } from "../components/category-icon";
import { EmptyState, ErrorNotice, Loading } from "../components/feedback";
import { NoteStateChip } from "../components/state-chip";
import { Dialog } from "../components/dialog";
import { useToast } from "../components/toast";
import * as notesApi from "../lib/api/notes-api";
import { noteState, type Note, type NoteState } from "../lib/api/types";
import { categoryMeta } from "../lib/ui/categories";
import { firstLine, relativeTime } from "../lib/ui/format";
import { useResource } from "../lib/ui/use-resource";

type Filter = "all" | NoteState;

const FILTERS: { value: Filter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
  { value: "edited", label: "Edited" },
  { value: "flagged", label: "Flagged" },
];

/** The API's own minimum. A shorter query is not sent, so it never comes back as a 422. */
const MIN_QUERY = 2;

export function KnowledgeScreen() {
  const navigate = useNavigate();
  const toast = useToast();
  const { category } = useSearch({ from: "/app/knowledge" });
  const [rawQuery, setRawQuery] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [pendingDelete, setPendingDelete] = useState<Note | null>(null);
  const [creating, setCreating] = useState(false);

  // Debounced, so typing "postgres" is one request rather than eight.
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(rawQuery.trim());
    }, 250);
    return () => {
      clearTimeout(timer);
    };
  }, [rawQuery]);

  // Only the two *stored* states narrow the request. `edited` and `flagged` are derived from
  // fields the server does not filter on, so asking for them here would silently drop rows.
  const serverStatus = filter === "draft" || filter === "published" ? filter : undefined;
  const effectiveQuery = query.length >= MIN_QUERY ? query : undefined;

  const load = useCallback(
    (signal: AbortSignal) =>
      notesApi.listAllNotes({ status: serverStatus, q: effectiveQuery }, signal),
    [serverStatus, effectiveQuery],
  );
  const notes = useResource(load, [serverStatus, effectiveQuery]);

  const inScope = (notes.data?.notes ?? []).filter(
    (note) => category === undefined || note.category === category,
  );
  const rows = inScope.filter((note) => {
    if (filter === "all") return true;
    if (filter === "flagged") return note.flagged;
    return noteState(note) === filter;
  });
  const publishedCount = inScope.filter((note) => note.status === "published").length;
  const heading = category === undefined ? "All knowledge" : categoryMeta(category).plural;

  async function createDraft(title: string) {
    setCreating(true);
    try {
      // Created inside a category, so a draft started under "Rules" is a rule rather than a
      // knowledge note the person then has to re-file.
      const created = await notesApi.createNote({ title, category });
      await navigate({ to: "/knowledge/$knowledgeId", params: { knowledgeId: created.id } });
    } catch {
      toast("Could not create that. Try again.");
      setCreating(false);
    }
  }

  async function confirmDelete() {
    const note = pendingDelete;
    if (note === null) return;
    setPendingDelete(null);
    try {
      await notesApi.deleteNote(note.id);
      toast("Deleted.");
      notes.reload();
    } catch {
      toast("Could not delete that. Try again.");
    }
  }

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">{heading}</h1>
        {notes.data !== null && (
          <span className="page-count">
            {rows.length} item{rows.length === 1 ? "" : "s"} · {publishedCount} published
          </span>
        )}
        <button
          className="button-primary page-head__action"
          type="button"
          disabled={creating}
          onClick={() => void createDraft("Untitled")}
        >
          New knowledge
        </button>
      </div>
      <p className="page-lede">
        Anything your team knows that the Handbook doesn&rsquo;t cover. Published knowledge is what
        your assistant can use — drafts stay yours.
      </p>

      {notes.data !== null && !notes.data.complete && (
        <p className="note-hint">
          This workspace holds more knowledge than one read can return, so what is below is the
          most recently updated part of it rather than all of it. Search to find the rest.
        </p>
      )}

      <input
        className="search-input"
        type="search"
        value={rawQuery}
        onChange={(event) => {
          setRawQuery(event.target.value);
        }}
        placeholder="Search titles, bodies and labels"
        aria-label="Search knowledge"
      />

      <div className="filter-row">
        <span className="filter-row__label">Status</span>
        {FILTERS.map((option) => (
          <button
            key={option.value}
            className="filter-pill"
            type="button"
            aria-pressed={filter === option.value}
            data-state={option.value === "all" ? undefined : option.value}
            onClick={() => {
              setFilter(option.value);
            }}
          >
            {option.label}
          </button>
        ))}
      </div>

      {(filter === "edited" || filter === "flagged") && rawQuery.trim().length >= MIN_QUERY && (
        <p className="note-hint">
          Searching narrows the rows the server returns; &ldquo;{filter}&rdquo; is then applied to
          those rows here, because it is worked out from timestamps the search cannot filter on.
        </p>
      )}

      {notes.loading && <Loading what="your knowledge" />}
      {notes.error !== null && <ErrorNotice error={notes.error} onRetry={notes.reload} />}

      {notes.data !== null && rows.length === 0 && notes.error === null && (
        <NoResults
          query={query}
          filtered={filter !== "all"}
          categoryName={category === undefined ? null : categoryMeta(category).plural}
          hasAny={inScope.length > 0}
          onClearFilters={() => {
            setFilter("all");
            setRawQuery("");
          }}
          onCreate={() => void createDraft(query.length > 0 ? query : "Untitled")}
        />
      )}

      {rows.length > 0 && (
        <ul className="note-list">
          {rows.map((note) => {
            const meta = categoryMeta(note.category);
            return (
              <li key={note.id}>
                <div className="note-row">
                  <span className="note-row__kind" data-category={note.category} aria-hidden="true">
                    <CategoryIcon category={note.category} />
                  </span>
                  <Link
                    className="note-row__open"
                    to="/knowledge/$knowledgeId"
                    params={{ knowledgeId: note.id }}
                  >
                    <span className="note-row__heading">
                      <span className="note-row__title">{note.title || "Untitled"}</span>
                      <span className="note-row__category" data-category={note.category}>
                        {meta.word}
                      </span>
                      {note.grade !== null && (
                        <span className="note-row__grade">{note.grade}</span>
                      )}
                    </span>
                    {firstLine(note.body) !== "" && (
                      <span className="note-row__excerpt">{firstLine(note.body)}</span>
                    )}
                    {note.tags.length > 0 && (
                      <span className="note-row__tags">
                        {note.tags.map((tag) => (
                          <span className="tag-chip" key={tag}>
                            {tag}
                          </span>
                        ))}
                      </span>
                    )}
                  </Link>
                  <div className="note-row__side">
                    <NoteStateChip state={noteState(note)} />
                    {note.last_returned_at !== null && (
                      <span className="note-row__used">
                        Used {relativeTime(note.last_returned_at)}
                      </span>
                    )}
                    <button
                      className="button-quiet"
                      type="button"
                      onClick={() => {
                        setPendingDelete(note);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {pendingDelete !== null && (
        <Dialog
          title={`Delete “${pendingDelete.title || "Untitled"}”?`}
          onClose={() => {
            setPendingDelete(null);
          }}
          footer={
            <>
              <button
                className="button-quiet"
                type="button"
                onClick={() => {
                  setPendingDelete(null);
                }}
              >
                Keep it
              </button>
              <button className="button-secondary" type="button" onClick={() => void confirmDelete()}>
                Delete
              </button>
            </>
          }
        >
          <p className="dialog__body">
            {pendingDelete.status === "published"
              ? "It’s published — your assistant loses access to it immediately. There is no version history, so this cannot be undone."
              : "It’s a draft, so nothing changes for your assistant. This cannot be undone."}
          </p>
        </Dialog>
      )}
    </>
  );
}

/**
 * Three different empty states, because they are three different facts.
 *
 * "Your workspace has nothing in it" invites you to write something. "Nothing matched what you
 * typed" invites you to clear the filter. "This category is empty" is neither — the workspace has
 * knowledge, just none of this kind. Showing the first when any of the others is true tells
 * somebody their knowledge base is empty when it is not, which, on a screen whose whole job is to
 * be trusted about what exists, is the worst thing it could say.
 */
function NoResults({
  query,
  filtered,
  categoryName,
  hasAny,
  onClearFilters,
  onCreate,
}: {
  query: string;
  filtered: boolean;
  categoryName: string | null;
  hasAny: boolean;
  onClearFilters: () => void;
  onCreate: () => void;
}) {
  const searching = query.length >= MIN_QUERY;

  if (!searching && !filtered && !hasAny && categoryName !== null) {
    return (
      <EmptyState
        headline={`Nothing filed under ${categoryName} yet.`}
        actions={
          <>
            <button className="button-primary" type="button" onClick={onCreate}>
              Write the first one
            </button>
            <Link className="button-quiet" to="/knowledge">
              See all knowledge
            </Link>
          </>
        }
      >
        <p className="empty__body">
          Your workspace has knowledge in it — none of it is a {categoryName.toLowerCase()} yet.
          Anything you write here can be re-filed from the editor later.
        </p>
      </EmptyState>
    );
  }

  if (!searching && !filtered && !hasAny) {
    return (
      <EmptyState
        eyebrow="Your first knowledge"
        headline="Write down one thing your team does that the Handbook doesn’t cover."
        actions={
          <button className="button-primary" type="button" onClick={onCreate}>
            Write the first one
          </button>
        }
      >
        <p className="empty__body">
          Registering seeded one example for you. If this is empty, it was deleted — nothing is
          hidden and nothing failed to load.
        </p>
      </EmptyState>
    );
  }

  return (
    <EmptyState
      headline={
        searching && filtered
          ? `Nothing matches “${query}” with this filter.`
          : searching
            ? `Nothing matches “${query}”.`
            : "Nothing matches this filter."
      }
      actions={
        <>
          {searching && (
            <button className="button-primary" type="button" onClick={onCreate}>
              Create &ldquo;{query}&rdquo;
            </button>
          )}
          {(filtered || searching) && (
            <button className="button-quiet" type="button" onClick={onClearFilters}>
              Clear filters
            </button>
          )}
        </>
      }
    />
  );
}
